import React, {
  Children,
  cloneElement,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  type CSSProperties
} from "react";
import { useWindowResizeListener } from "Hooks/useWindowResizeListener";
import debounce from "./utils/debounce";
import transformProperty from "./utils/transformProperty";

type PerPageConfig = number | Record<string, number>;

export type ReactSiemaProps = {
  resizeDebounce?: number;
  duration?: number;
  easing?: string;
  perPage?: PerPageConfig;
  startIndex?: number;
  draggable?: boolean;
  threshold?: number;
  loop?: boolean;
  children?: React.ReactNode;
  updateAfterDrag?: () => void;
  onResize?: (perPage: number) => void;
};

export type ReactSiemaImperativeHandle = {
  readonly currentSlide: number;
  getPerPage: () => number;
  next: () => void;
  prev: () => void;
  goTo: (index: number) => void;
};

type DragState = { start: number; end: number };

const SLIDE_CHILD_STYLE: React.CSSProperties = { float: "left" };

function setStyle(
  target: HTMLElement,
  styles: CSSProperties & Record<string, string>
) {
  Object.keys(styles).forEach(attribute => {
    (target.style as unknown as Record<string, string>)[attribute] = styles[
      attribute as keyof typeof styles
    ] as string;
  });
}

const ReactSiema = forwardRef<
  ReactSiemaImperativeHandle,
  ReactSiemaProps
>(function ReactSiema(
  {
    resizeDebounce = 250,
    duration = 200,
    easing = "ease-out",
    perPage: perPageProp = 1,
    startIndex = 0,
    draggable = true,
    threshold = 20,
    loop = false,
    children,
    updateAfterDrag = () => {},
    onResize = () => {}
  },
  ref
) {
  const selectorRef = useRef<HTMLDivElement | null>(null);
  const sliderFrameRef = useRef<HTMLDivElement | null>(null);

  const currentSlideRef = useRef(startIndex);
  const perPageRef = useRef(1);
  const selectorWidthRef = useRef(0);
  const innerElementsRef = useRef<HTMLElement[]>([]);
  const pointerDownRef = useRef(false);
  const dragRef = useRef<DragState>({ start: 0, end: 0 });

  const propsRef = useRef({ updateAfterDrag, onResize });
  propsRef.current = { updateAfterDrag, onResize };

  const configRef = useRef({
    resizeDebounce,
    duration,
    easing,
    perPage: perPageProp,
    startIndex,
    draggable,
    threshold,
    loop
  });
  configRef.current = {
    resizeDebounce,
    duration,
    easing,
    perPage: perPageProp,
    startIndex,
    draggable,
    threshold,
    loop
  };

  const resolveSlidesNumber = useCallback(() => {
    const { perPage } = configRef.current;
    if (typeof perPage === "number") {
      perPageRef.current = perPage;
    } else if (perPage !== null && typeof perPage === "object") {
      perPageRef.current = 1;
      for (const viewport of Object.keys(perPage)) {
        if (window.innerWidth > Number(viewport)) {
          perPageRef.current = perPage[viewport] as number;
        }
      }
    }
  }, []);

  const slideToCurrent = useCallback(() => {
    const frame = sliderFrameRef.current;
    if (!frame) return;
    (frame.style as unknown as Record<string, string>)[transformProperty] =
      `translate3d(-${
      currentSlideRef.current *
      (selectorWidthRef.current / perPageRef.current)
    }px, 0, 0)`;
  }, []);

  const init = useCallback(() => {
    const selector = selectorRef.current;
    const sliderFrame = sliderFrameRef.current;
    if (!selector || !sliderFrame) return;

    selectorWidthRef.current = selector.getBoundingClientRect().width;
    innerElementsRef.current = Array.prototype.slice.call(
      sliderFrame.children
    ) as HTMLElement[];
    resolveSlidesNumber();

    const cfg = configRef.current;
    const inner = innerElementsRef.current;
    const sw = selectorWidthRef.current;
    const pp = perPageRef.current;

    setStyle(sliderFrame, {
      width: `${(sw / pp) * inner.length}px`,
      WebkitTransition: `all ${cfg.duration}ms ${cfg.easing}`,
      transition: `all ${cfg.duration}ms ${cfg.easing}`
    });

    for (let i = 0; i < inner.length; i += 1) {
      setStyle(inner[i], {
        width: `${100 / inner.length}%`
      });
    }

    slideToCurrent();
  }, [resolveSlidesNumber, slideToCurrent]);

  const resize = useCallback(() => {
    const selector = selectorRef.current;
    const sliderFrame = sliderFrameRef.current;
    if (!selector || !sliderFrame) return;

    resolveSlidesNumber();
    selectorWidthRef.current = selector.getBoundingClientRect().width;
    const inner = innerElementsRef.current;
    const sw = selectorWidthRef.current;
    const pp = perPageRef.current;

    setStyle(sliderFrame, {
      width: `${(sw / pp) * inner.length}px`
    });

    propsRef.current.onResize(pp);
  }, [resolveSlidesNumber]);

  const clearDrag = useCallback(() => {
    dragRef.current = { start: 0, end: 0 };
  }, []);

  const prev = useCallback(() => {
    const cfg = configRef.current;
    const inner = innerElementsRef.current;
    const pp = perPageRef.current;
    if (currentSlideRef.current === 0 && cfg.loop) {
      currentSlideRef.current = inner.length - pp;
    } else {
      currentSlideRef.current = Math.max(currentSlideRef.current - 1, 0);
    }
    slideToCurrent();
  }, [slideToCurrent]);

  const next = useCallback(() => {
    const cfg = configRef.current;
    const inner = innerElementsRef.current;
    const pp = perPageRef.current;
    if (
      currentSlideRef.current === inner.length - pp &&
      cfg.loop
    ) {
      currentSlideRef.current = 0;
    } else {
      currentSlideRef.current = Math.min(
        currentSlideRef.current + 1,
        inner.length - pp
      );
    }
    slideToCurrent();
  }, [slideToCurrent]);

  const goTo = useCallback(
    (index: number) => {
      const inner = innerElementsRef.current;
      currentSlideRef.current = Math.min(
        Math.max(index, 0),
        inner.length - 1
      );
      slideToCurrent();
    },
    [slideToCurrent]
  );

  const runUpdateAfterDrag = useCallback(() => {
    const cfg = configRef.current;
    const movement = dragRef.current.end - dragRef.current.start;
    if (movement > 0 && Math.abs(movement) > cfg.threshold) {
      prev();
    } else if (movement < 0 && Math.abs(movement) > cfg.threshold) {
      next();
    }
    slideToCurrent();
    propsRef.current.updateAfterDrag();
  }, [prev, next, slideToCurrent]);

  useImperativeHandle(
    ref,
    () => ({
      get currentSlide() {
        return currentSlideRef.current;
      },
      getPerPage: () => perPageRef.current,
      next,
      prev,
      goTo
    }),
    [next, prev, goTo]
  );

  const triggerResizeLayout = useCallback(() => {
    resize();
    slideToCurrent();
  }, [resize, slideToCurrent]);

  const debouncedResize = useMemo(
    () => debounce(triggerResizeLayout, resizeDebounce),
    [triggerResizeLayout, resizeDebounce]
  );

  useWindowResizeListener(() => {
    debouncedResize();
  }, true);

  useLayoutEffect(() => {
    init();
  });

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
    pointerDownRef.current = true;
    dragRef.current.start = e.touches[0].pageX;
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();
      pointerDownRef.current = false;
      const frame = sliderFrameRef.current;
      if (!frame) return;
      const cfg = configRef.current;
      setStyle(frame, {
        WebkitTransition: `all ${cfg.duration}ms ${cfg.easing}`,
        transition: `all ${cfg.duration}ms ${cfg.easing}`
      });
      if (dragRef.current.end) {
        runUpdateAfterDrag();
      }
      clearDrag();
    },
    [clearDrag, runUpdateAfterDrag]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();
      if (!pointerDownRef.current) return;
      const frame = sliderFrameRef.current;
      if (!frame) return;
      dragRef.current.end = e.touches[0].pageX;
      const cfg = configRef.current;
      setStyle(frame, {
        WebkitTransition: `all 0ms ${cfg.easing}`,
        transition: `all 0ms ${cfg.easing}`,
        [transformProperty]: `translate3d(${
          (currentSlideRef.current *
            (selectorWidthRef.current / perPageRef.current) +
            (dragRef.current.start - dragRef.current.end)) *
          -1
        }px, 0, 0)`
      });
    },
    []
  );

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    pointerDownRef.current = true;
    dragRef.current.start = e.pageX;
  }, []);

  const onMouseUp = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      pointerDownRef.current = false;
      const frame = sliderFrameRef.current;
      if (!frame) return;
      const cfg = configRef.current;
      setStyle(frame, {
        cursor: "-webkit-grab",
        WebkitTransition: `all ${cfg.duration}ms ${cfg.easing}`,
        transition: `all ${cfg.duration}ms ${cfg.easing}`
      });
      if (dragRef.current.end) {
        runUpdateAfterDrag();
      }
      clearDrag();
    },
    [clearDrag, runUpdateAfterDrag]
  );

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (!pointerDownRef.current) return;
    const frame = sliderFrameRef.current;
    if (!frame) return;
    dragRef.current.end = e.pageX;
    const cfg = configRef.current;
    setStyle(frame, {
      cursor: "-webkit-grabbing",
      WebkitTransition: `all 0ms ${cfg.easing}`,
      transition: `all 0ms ${cfg.easing}`,
      [transformProperty]: `translate3d(${
        (currentSlideRef.current *
          (selectorWidthRef.current / perPageRef.current) +
          (dragRef.current.start - dragRef.current.end)) *
        -1
      }px, 0, 0)`
    });
  }, []);

  const onMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      if (!pointerDownRef.current) return;
      pointerDownRef.current = false;
      dragRef.current.end = e.pageX;
      const frame = sliderFrameRef.current;
      if (!frame) return;
      const cfg = configRef.current;
      setStyle(frame, {
        cursor: "-webkit-grab",
        WebkitTransition: `all ${cfg.duration}ms ${cfg.easing}`,
        transition: `all ${cfg.duration}ms ${cfg.easing}`
      });
      runUpdateAfterDrag();
      clearDrag();
    },
    [clearDrag, runUpdateAfterDrag]
  );

  const eventProps = useMemo(
    () => ({
      onTouchStart,
      onTouchEnd,
      onTouchMove,
      onMouseDown,
      onMouseUp,
      onMouseLeave,
      onMouseMove
    }),
    [
      onTouchEnd,
      onTouchMove,
      onMouseDown,
      onMouseUp,
      onMouseLeave,
      onMouseMove,
      onTouchStart
    ]
  );

  return (
    <div ref={selectorRef} className="react-siema-container" {...eventProps}>
      <div ref={sliderFrameRef}>
        {Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) return child;
          const el = child as React.ReactElement<{
            style?: React.CSSProperties;
          }>;
          return cloneElement(el, {
            key: index,
            style: { ...SLIDE_CHILD_STYLE, ...el.props.style }
          });
        })}
      </div>
    </div>
  );
});

ReactSiema.displayName = "ReactSiema";

export default ReactSiema;
